'use client'

/**
 * Utility functions to apply theme consistently throughout the application
 */

/**
 * Apply theme to document body and store in localStorage
 */
export function applyTheme(theme: string) {
  // If SSR, return early
  if (typeof window === 'undefined') return;
  
  // Set theme class on document body
  const root = window.document.documentElement;
  
  // Get system preference
  const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
  
  // Apply data-theme attribute (for next-themes or similar libraries)
  root.dataset.theme = theme;
  
  // Clear previous theme classes
  const classes = root.classList;
  classes.remove('light', 'dark');
  
  // Add the new theme class
  const themeClass = theme === 'system' ? systemPreference : theme;
  classes.add(themeClass);
  
  // Store preference in localStorage
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('theme', theme);
  }

  // List of all available themes
  const allThemes = ['neutral', 'gulf', 'triadic', 'redbull'];
  
  // Remove any existing theme classes
  allThemes.forEach(t => {
    if (t !== theme) { // Don't remove the current theme class
      root.classList.remove(t);
    }
  });
  
  // Add the new theme class to the root element
  root.classList.add(theme);
  
  // Special handling for neutral theme - ensure buttons have no border radius
  if (theme === 'neutral') {
    root.style.setProperty('--radius-button', 'var(--radius-none)');
  }
}

/**
 * Ensure the neutral theme is always applied by default if no theme is specified
 */
export function ensureDefaultTheme() {
  if (typeof document === 'undefined') return;
  
  // Get current theme
  const currentTheme = document.documentElement.getAttribute('data-theme');
  
  // If no theme is set, apply neutral
  if (!currentTheme) {
    applyTheme('neutral');
  }
}